import React, { useState, useEffect, useCallback } from "react";
import {
  Typography,
  Box,
  Button,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { ArrowBack, CheckCircle, Save } from "@mui/icons-material";
import { useNavigate, useLocation } from "react-router-dom";
import { getConsentFormSelection, submitConsents } from "utils/api";
import FocusedFlowPage from "components/FocusedFlowPage";
import { usePatient } from "./PatientProvider";
import { BIOBANK_ROUTES } from "../../../constants/routes";

const ConsentCheck = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { patientData } = usePatient();

  const [patientId, setPatientId] = useState("");
  const [patientName, setPatientName] = useState("");
  const [patientLanguage, setPatientLanguage] = useState("en");
  const [encounterId, setEncounterId] = useState(null);
  const [nextStep, setNextStep] = useState(BIOBANK_ROUTES.PARTICIPANT_CHECKIN);
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  
  // Consent Data
  const [generalConsents, setGeneralConsents] = useState([]);
  const [scheduledStudyConsents, setScheduledStudyConsents] = useState([]);
  const [otherStudyConsents, setOtherStudyConsents] = useState([]);
  
  // State for which forms are newly checked by the user
  const [checkedForms, setCheckedForms] = useState(new Set());
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  useEffect(() => {
    const sourcePatient = location.state?.patient || location.state?.patientData || location.state?.user || patientData || {};
    
    const name = sourcePatient.patientName || sourcePatient.name || 
                 (sourcePatient.firstName && sourcePatient.lastName ? `${sourcePatient.firstName} ${sourcePatient.lastName}` : "Patient");
    const id = location.state?.patientId || sourcePatient.patientId || sourcePatient.id || patientData?.patientId || patientData?.id || "unknown";
    const language = sourcePatient.preferredLanguage || location.state?.patientLanguage || "en";
    const encId = location.state?.encounterId || sourcePatient.encounterId || patientData?.encounterId || null;
    const nStep = location.state?.nextStep || BIOBANK_ROUTES.PARTICIPANT_CHECKIN;

    setPatientName(name);
    setPatientId(id);
    setPatientLanguage(language);
    setEncounterId(encId);
    setNextStep(nStep);
  }, [location.state, patientData]);

  const fetchConsents = useCallback(async () => {
    if (!patientId) return;
    try {
      setLoading(true);
      setError(null);
      const selectionResult = await getConsentFormSelection(patientId, encounterId, patientLanguage);
      setGeneralConsents(selectionResult.generalConsents || []);
      setScheduledStudyConsents(selectionResult.scheduledStudyConsents || []);
      setOtherStudyConsents(selectionResult.otherStudyConsents || []);
      setCheckedForms(new Set());
    } catch (err) {
      console.error("Error loading consent selection:", err);
      setError(err.message || "Failed to load consents");
    } finally {
      setLoading(false);
    }
  }, [patientId, encounterId, patientLanguage]);

  useEffect(() => {
    fetchConsents();
  }, [fetchConsents]);

  const handleCheckboxChange = (formId) => {
    setCheckedForms((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(formId)) {
        newSet.delete(formId);
      } else {
        newSet.add(formId);
      }
      return newSet;
    });
  };

  const handleSubmit = async () => {
    setShowConfirmDialog(true);
  };

  const handleConfirmAction = async () => {
    setShowConfirmDialog(false);
    
    if (checkedForms.size > 0) {
      try {
        setSubmitting(true);
        setError(null);
        
        const consentsToSubmit = Array.from(checkedForms).map(formId => ({
          consentFormId: formId,
          consentDate: new Date().toISOString()
        }));

        await submitConsents(patientId, consentsToSubmit);
      } catch (err) {
        console.error("Error submitting consents:", err);
        setError(err.message || "Failed to save consents. Please try again.");
        setSubmitting(false);
        return; // Don't navigate on error
      }
    }

    navigate(nextStep, { 
      state: { 
        ...location.state,
        patient: location.state?.patient || location.state?.user || location.state?.patientData || patientData,
        user: location.state?.user || location.state?.patient || location.state?.patientData || patientData,
        refresh: true 
      } 
    });
  };

  const handleBack = () => {
    // Back should always go to the Confirm Patient Information page
    navigate(-1);
  };

  const renderConsentGroup = (title, forms) => {
    if (!forms || forms.length === 0) return null;
    
    return (
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, color: "#500000", mb: 2 }}>
          {title}
        </Typography>
        <Card variant="outlined" sx={{ backgroundColor: "#f9f9f9" }}>
          <CardContent>
            <FormGroup>
              {forms.map((item) => {
                const formId = item.consentForm?.consentFormId;
                const displayName = item.consentForm?.irbStudy 
                  ? `${item.consentForm.irbStudy} - ${item.consentForm.consentName || "Consent Form"}`
                  : item.consentForm?.consentName || "Consent Form";

                if (item.satisfied) {
                  return (
                    <Box key={formId} sx={{ display: 'flex', alignItems: 'center', py: 1, gap: 1 }}>
                      <CheckCircle color="success" />
                      <Typography variant="body1" sx={{ color: "text.secondary", fontWeight: 500 }}>
                        {displayName} (Completed)
                      </Typography>
                    </Box>
                  );
                }

                return (
                  <FormControlLabel
                    key={formId}
                    control={
                      <Checkbox 
                        checked={checkedForms.has(formId)} 
                        onChange={() => handleCheckboxChange(formId)}
                        disabled={!item.selectable || submitting}
                      />
                    }
                    label={displayName}
                    sx={{ py: 0.5 }}
                  />
                );
              })}
            </FormGroup>
          </CardContent>
        </Card>
      </Box>
    );
  };

  if (loading) {
    return (
      <FocusedFlowPage
        title="Patient Consent Forms"
        onClose={handleBack}
        maxWidth={800}
        actions={[]}
      >
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 8 }}>
          <CircularProgress />
        </Box>
      </FocusedFlowPage>
    );
  }

  return (
    <FocusedFlowPage
      title="Patient Consent Forms"
      subtitle="Select the missing consent forms that the patient has signed or completed."
      onClose={handleBack}
      maxWidth={800}
      actions={[
        <Button
          key="back"
          variant="outlined"
          startIcon={<ArrowBack />}
          onClick={handleBack}
          disabled={submitting}
        >
          Back
        </Button>,
        <Button
          key="submit"
          variant="contained"
          startIcon={submitting ? <CircularProgress size={20} color="inherit" /> : <Save />}
          onClick={handleSubmit}
          disabled={submitting}
          sx={{ backgroundColor: "#500000", "&:hover": { backgroundColor: "#732f2f" } }}
        >
          {checkedForms.size > 0 ? "Save Consents" : "Continue"}
        </Button>,
      ]}
    >
      <Dialog
        open={showConfirmDialog}
        onClose={() => setShowConfirmDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ color: "#500000", fontWeight: 600 }}>
          {checkedForms.size === 0 ? "Confirm No Consents Selected" : "Confirm Consents Selection"}
        </DialogTitle>
        <DialogContent>
          {checkedForms.size === 0 ? (
            <>
              <Typography variant="body1">
                You have not selected any consent forms to sign for this participant.
              </Typography>
              <Typography variant="body2" sx={{ mt: 2, color: "text.secondary" }}>
                Are you sure you want to continue without collecting any new consents?
              </Typography>
            </>
          ) : (
            <>
              <Typography variant="body1">
                Clicking Confirm & Continue indicates you have received the
                participant's signed paper consent form(s) for the following:
              </Typography>
              <Box sx={{ mt: 2, pl: 2 }}>
                <ul style={{ margin: 0 }}>
                  {Array.from(checkedForms).map(formId => {
                    const form = [
                      ...generalConsents,
                      ...scheduledStudyConsents,
                      ...otherStudyConsents
                    ].find(f => f.consentForm?.consentFormId === formId);
                    
                    return (
                      <li key={formId}>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: "#3E3E3E" }}>
                          {form?.consentForm?.consentName || formId} 
                          {form?.consentForm?.irbStudy ? ` (${form.consentForm.irbStudy})` : ""}
                        </Typography>
                      </li>
                    );
                  })}
                </ul>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button
            onClick={() => setShowConfirmDialog(false)}
            variant="outlined"
            disabled={submitting}
            sx={{
              borderColor: "#500000",
              color: "#500000",
            }}
          >
            Go Back
          </Button>
          <Button
            onClick={handleConfirmAction}
            variant="contained"
            disabled={submitting}
            autoFocus
            sx={{
              backgroundColor: "#500000",
              "&:hover": {
                backgroundColor: "#3a0000",
              },
            }}
          >
            {submitting ? "Saving..." : "Yes, Continue"}
          </Button>
        </DialogActions>
      </Dialog>
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Patient Information */}
      <Card sx={{ mb: 4, backgroundColor: "#f0f4f8" }}>
        <CardContent>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Patient Information
          </Typography>
          <Box sx={{ display: "flex", gap: 4 }}>
            <Typography variant="body1"><strong>Name:</strong> {patientName}</Typography>
            <Typography variant="body1"><strong>Language:</strong> {patientLanguage.toUpperCase()}</Typography>
          </Box>
        </CardContent>
      </Card>

      {/* Consent Groups */}
      {renderConsentGroup("General Consents", generalConsents)}
      {renderConsentGroup(encounterId ? "Scheduled Study Consents" : "Study Consents", scheduledStudyConsents)}
      {renderConsentGroup("Other Study Consents", otherStudyConsents)}

      {generalConsents.length === 0 && scheduledStudyConsents.length === 0 && otherStudyConsents.length === 0 && (
        <Alert severity="info">
          No consent forms available for this patient.
        </Alert>
      )}

    </FocusedFlowPage>
  );
};

export default ConsentCheck;
